import { createAsyncReducer } from '../../standard-actions/reducers';
import {
    ILoadOrdersStartAction,
    ILoadOrdersSuccessAction,
    LoadOrdersErrorAction,
    ISortOrderAction,
    IOrderReferenceAction,
    IApplySelectColumnsAction
} from '../actions/load-orders';
import { IOrderTrackerState } from '../state';
import { initialState } from '../state/initial-state';
import { filterOrders, filterLocationOrders, searchOrders, searchLocationOrders } from '../methods/search-orders';
import { sortOrders, sortLocationOrders } from '../methods/sort-orders';
import { mapOrder, mapLocationOrder } from '../methods/load-orders-payload';
import cloneDeep from 'lodash-es/cloneDeep';

export type LoadOrdersAction
    = ILoadOrdersStartAction
    | ILoadOrdersSuccessAction
    | LoadOrdersErrorAction;

export type SortOrderAction = ISortOrderAction;

export type ApplySelectColumnsAction = IApplySelectColumnsAction;

const init = (state: IOrderTrackerState, action: LoadOrdersAction) => ({
    ...state,
    activeOrders: {
        ...state.activeOrders,
        data: {
            orders: [],
            original_orders: [],
            isPartialResponse: null,
            locationOrders: []
        },
        status: action.status
    }
});

const success = (state: IOrderTrackerState, action: LoadOrdersAction) => {
    const originalOrders = (action as ILoadOrdersSuccessAction).data.original_orders;
    const formattedOrderProducts = (action as ILoadOrdersSuccessAction).data.format_order_products;
    return ({
        ...state,
        activeOrders: {
            ...state.activeOrders,
            status: action.status,
            data: {
                ...state.activeOrders.data,
                original_orders: originalOrders,
                orders: mapOrder(originalOrders),
                locationOrders: mapLocationOrder(originalOrders),
                isPartialResponse: (action as ILoadOrdersSuccessAction).data.isPartialResponse,
                format_order_products: formattedOrderProducts
            }
        }
    })
};

const failure = (state: IOrderTrackerState, action: LoadOrdersAction) => ({
    ...state,
    activeOrders: {
        ...initialState.activeOrders,
        status: action.status,
        error: (action as LoadOrdersErrorAction).error
    }
});

export const sortOrdersReducer = (state: IOrderTrackerState, action: ISortOrderAction) => {
    let tabs = {};
    if (action.tabName === 'Locations') {
        tabs = {
            locations: {
                ...state.activeOrders.tabs.locations,
                sort: action.data
            }
        }
    } else {
        tabs = {
            orders: {
                ...state.activeOrders.tabs.orders,
                sort: action.data
            }
        }
    }

    return ({
        ...state,
        activeOrders: {
            ...state.activeOrders,
            tabs: {
                ...state.activeOrders.tabs,
                ...tabs
            }
        }
    })
}

export const applySelectColumnsReducer = (state: IOrderTrackerState, action: IApplySelectColumnsAction) => {
    let tabs = {};
    if (action.tabName === 'Locations') {
        tabs = {
            locations: {
                ...state.activeOrders.tabs.locations,
                selectedColumns: action.data
            }
        }
    } else {
        tabs = {
            orders: {
                ...state.activeOrders.tabs.orders,
                selectedColumns: action.data
            }
        }
    }

    return ({
        ...state,
        activeOrders: {
            ...state.activeOrders,
            tabs: {
                ...state.activeOrders.tabs,
                ...tabs
            }
        }
    });
}

export const loadPagination = (state, action) => {
    let tabs = {};
    if (action.tabName === 'Locations') {
        tabs = {
            locations: {
                ...state.activeOrders.tabs.locations,
                paginationOrder: action.data
            }
        }
    } else {
        tabs = {
            orders: {
                ...state.activeOrders.tabs.orders,
                paginationOrder: action.data
            }
        }
    }

    return ({
        ...state,
        activeOrders: {
            ...state.activeOrders,
            tabs: {
                ...state.activeOrders.tabs,
                ...tabs
            }
        }
    })
};

export const setActiveTab = (state, action) => ({
    ...state,
    tabState: {
        activeTab: action.tabName
    }
})

export const updateOrderTabsReducer = (state: IOrderTrackerState, action: IOrderReferenceAction) => {
    const filters = state.search.orders.filters;
    const searchString = state.search.orders.searchString;

    const isFilterApplied = (filters && (filters.location && filters.location.length) ||
        (filters.productName && filters.productName.length) ||
        (filters.status && filters.status.length)) ? true : false;

    let filterdActiveOrders = cloneDeep(state.activeOrders.data.original_orders);
    let orders = state.activeOrders.data.orders;

    let locationOrders = state.activeOrders.data.locationOrders;

    if (state.tabState.activeTab === 'Locations') {
        locationOrders = mapLocationOrder(filterdActiveOrders);

        if (isFilterApplied) {
            locationOrders = filterLocationOrders(locationOrders, filters);
        }

        if (searchString.length) {
            locationOrders = searchLocationOrders(locationOrders, searchString, state.search.orders.isInternational);
        }

        locationOrders = sortLocationOrders({
            activeTab: state.tabState.activeTab,
            sort: state.activeOrders.tabs.locations.sort
        }, locationOrders);
    } else {
        if (isFilterApplied) {
            filterdActiveOrders = filterOrders(filterdActiveOrders, filters);
        }
        orders = mapOrder(filterdActiveOrders);
        if (searchString.length) {
            orders = searchOrders(orders, searchString, state.search.orders.isInternational);
        }

        orders = sortOrders({
            activeTab: state.tabState.activeTab,
            sort: state.activeOrders.tabs.orders.sort
        }, orders);
    }

    const finalData = {
        ...state,
        activeOrders: {
            ...state.activeOrders,
            data: {
                ...state.activeOrders.data,
                orders,
                locationOrders
            }
        }
    };
    return finalData
}

export const updateOrderReferenceReducer = (state: IOrderTrackerState, action: IOrderReferenceAction) => {
    const filters = state.search.orders.filters;
    const searchString = state.search.orders.searchString;

    const isFilterApplied = (filters && (filters.location && filters.location.length) ||
        (filters.productName && filters.productName.length) ||
        (filters.status && filters.status.length)) ? true : false;

    let filterdActiveOrders = cloneDeep(state.activeOrders.data.original_orders);
    let orders = state.activeOrders.data.orders;
    let locationOrders = state.activeOrders.data.locationOrders;
    // const activeOrderSort = action.parameters && action.parameters.sort && action.parameters.sort.active || '';

    for (let i = 0; i <= orders.length - 1; i++) {
        if (orders[i].number === action.data.orderNumber) {
            orders[i].yourReference = action.data.yourReference;
            state.activeOrders.data.original_orders[i].yourReference = action.data.yourReference;
        }
    }

    for (let i = 0; i <= state.activeOrders.data.original_orders.length - 1; i++) {
        if (state.activeOrders.data.original_orders[i].number === action.data.orderNumber) {
            state.activeOrders.data.original_orders[i].yourReference = action.data.yourReference;
        }
    }

    for (let j = 0; j <= locationOrders.length - 1; j++) {
        if (locationOrders[j].number === action.data.orderNumber) {
            locationOrders[j].yourReference = action.data.yourReference;
        }

    }

    if (state.tabState.activeTab === 'Locations') {
        locationOrders = mapLocationOrder(filterdActiveOrders);

        if (isFilterApplied) {
            locationOrders = filterLocationOrders(locationOrders, filters);
        }

        if (searchString.length) {
            locationOrders = searchLocationOrders(locationOrders, searchString, state.search.orders.isInternational);
        }

        locationOrders = sortLocationOrders({
            activeTab: state.tabState.activeTab,
            sort: state.activeOrders.tabs.locations.sort
        }, locationOrders);
    } else {
        if (isFilterApplied) {
            filterdActiveOrders = filterOrders(filterdActiveOrders, filters);
        }

        orders = mapOrder(filterdActiveOrders);

        if (searchString.length) {
            orders = searchOrders(orders, searchString, state.search.orders.isInternational);
        }

        orders = sortOrders({
            activeTab: state.tabState.activeTab,
            sort: state.activeOrders.tabs.orders.sort
        }, orders);
    }

    return ({
        ...state,
        activeOrders: {
            ...state.activeOrders,
            data: {
                ...state.activeOrders.data,
                ...orders,
                ...locationOrders
            }
        }
    })
}

export const loadOrdersReducer = createAsyncReducer(initialState, init, success, failure);
